import { Stack, Typography } from "@mui/material";
import { Handle, Position } from "@xyflow/react";
import type { ReactNode } from "react";

import type { DecisionRuleNode, DecisionStatement, RuleNodeProps } from "../types/nodes";
import { formatCollectionValueFromLabel } from "../utils/collection-utils";
import { formatLiteralEdgeWhitespace } from "../utils/format-literal-edge-whitespace";
import type { ScreenFieldRef } from "../utils/data-field-type-utils";
import { useRuleScreenFieldDisplay } from "./rule-screen-field-display-context";
import BaseNode from "./BaseNode";

type DecisionNodeProps = RuleNodeProps<DecisionRuleNode>;

function resolveStatementValueDisplay(
	statement: DecisionStatement,
	formatScreenField: (
		name: string | undefined,
		fieldId?: string,
		screenFieldRef?: ScreenFieldRef,
	) => string | undefined,
): ReactNode {
	const valueCollectionName =
		statement.valueCollectionName ?? statement.valueResourceCollection?.name;
	const entityDisplay =
		(statement.valueResourceCollectionId && valueCollectionName
			? formatCollectionValueFromLabel(valueCollectionName)
			: undefined) ??
		formatScreenField(
			statement.valueScreenTableField?.name ??
				statement.valueScreenField?.name,
			statement.valueScreenFieldId,
			statement.valueScreenTableField ?? statement.valueScreenField,
		) ??
		statement.valueAdvancedTrigger?.name ??
		statement.valueDatabaseTableField?.name ??
		statement.valueFormulaVariableName ??
		statement.valueDatabaseSingleRowVariableName ??
		statement.valueSimpleVariableName ??
		statement.valueGlobalConstantName ??
		statement.valueGlobalConstant;

	if (entityDisplay) return entityDisplay;

	const literal =
		typeof statement.plainTextValue === "string"
			? statement.plainTextValue
			: typeof statement.value === "string"
				? statement.value
				: "";

	if (!literal) return "";

	return formatLiteralEdgeWhitespace(literal);
}

const DecisionNode = (props: DecisionNodeProps) => {
	const { data } = props;
	const statements = data.statements ?? [];
	const screenFieldDisplay = useRuleScreenFieldDisplay();

	const formatScreenField = (
		name: string | undefined,
		fieldId?: string,
		screenFieldRef?: ScreenFieldRef,
	): string | undefined => {
		if (!name) return undefined;
		return screenFieldDisplay
			? screenFieldDisplay.formatScreenFieldLabel(name, fieldId, screenFieldRef)
			: name;
	};

	return (
		<BaseNode {...props}>
			<Stack spacing={1.5}>
				{statements.length ? (
					<Stack spacing={0.75}>
						{statements.map((statement, index) => (
							<Typography
								variant="body2"
								color="text.primary"
								key={String(statement.id ?? index)}
							>
								{index + 1}.{" "}
								{statement.databaseTableField?.name ??
                  formatScreenField(
                  	statement.screenTableField?.name,
                  	statement.screenFieldId,
                  	statement.screenTableField,
                  ) ??
                  statement.advancedTrigger?.name ??
                  statement.resourceCollection?.name ??
                  statement.collectionName ??
                  statement.formulaVariableName ??
                  statement.simpleVariableName ??
                  statement.resourceReactionName ??
                  ""}{" "}
								{statement.operator ?? ""}{" "}
								{resolveStatementValueDisplay(statement, formatScreenField)}
							</Typography>
						))}
					</Stack>
				) : (
					<Typography variant="body2" color="text.disabled">
            No decision statements set
					</Typography>
				)}
			</Stack>

			<Handle type="target" position={Position.Top} id="in" />
			<Handle
				type="source"
				position={Position.Bottom}
				id="true"
				isConnectableStart={false}
				style={{ left: "30%", width: 14, height: 14, background: "transparent", border: "none", opacity: 0 }}
			/>
			<Handle
				type="source"
				position={Position.Bottom}
				id="false"
				isConnectableStart={false}
				style={{ left: "70%", width: 14, height: 14, background: "transparent", border: "none", opacity: 0 }}
			/>
		</BaseNode>
	);
};

export default DecisionNode;
